import { ComponentProps, ReactNode, useCallback, useMemo, useState } from "react";
import { Control, Controller, FieldError, FieldPath, FieldValues, Merge } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { components, GroupBase, SelectComponentsConfig } from "react-select";
import AsyncCreatableSelect from "react-select/async-creatable";

import Button from "@components/buttons/Button";
import ConditionalTooltip from "@components/ConditionalTooltip";
import Error from "@components/fields/Error";
import RequiredFieldIndicator from "@components/fields/RequiredFieldIndicator";
import styles from "@components/fields/Select.module.css";
import LargeLabel from "@components/labels/LargeLabel";
import SmallLabel from "@components/labels/SmallLabel";
import Tag from "@components/labels/Tag";
import Icon from "@components/text/Icon";
import IconBase from "@components/text/IconBase";
import useLazyQueryWithPromise from "@hooks/useLazyQueryWithPromise";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import {
  AiContentFieldInput,
  GetTagsDocument,
  GetTagsQuery,
  GetTagsQueryVariables,
  RecommendedTagsGenerateDocument,
  RecommendedTagsGenerateMutation,
  RecommendedTagsGenerateMutationVariables,
  TagTypeEnum
} from "@typing/Generated";
import { selectStyles } from "@utils/formUtils";
import { sortByStringField } from "@utils/modelUtils";
import { capitalizeFirstLetter, joinListWithCommasAndAnd } from "@utils/stringUtils";

type Option = {
  color?: string;
  label: string;
  value: string;
};

type Color = "dark" | "grey";

type Props<FormData extends FieldValues> = ComponentProps<typeof AsyncCreatableSelect> & {
  aiInputs?: AiContentFieldInput[];
  aiTagTypes?: TagTypeEnum[];
  allowAny?: boolean;
  centeredLabel?: boolean;
  color?: Color;
  control: Control<FormData>;
  disabled?: boolean;
  errors: Merge<FieldError, (FieldError | undefined)[]> | undefined;
  label?: string | ReactNode;
  name: FieldPath<FormData>;
  onValueChange?: (newValue: string[] | string) => void;
  required?: boolean;
  single?: boolean;
  smallerLabel?: boolean;
  subLabel?: string | ReactNode;
  tagType: TagTypeEnum;
};

const TagSelect = <FormData extends FieldValues>({
  aiInputs,
  aiTagTypes,
  allowAny = false,
  color = "dark",
  control,
  disabled = false,
  errors,
  label,
  name,
  onValueChange,
  required,
  single = false,
  smallerLabel = false,
  subLabel,
  tagType,
  ...selectProps
}: Props<FormData>) => {
  const [aiLoading, setAiLoading] = useState(false);
  const intl = useIntl();
  const [recommendedTags, setRecommendedTags] = useState<string[]>([]);
  const aiInputsFormatted = aiInputs?.map(input => ({
    description: input.description,
    // Only get the first 1000 characters and remove any element tags
    value: `"${input.value.replace(/(<([^>]+)>)/gi, "").slice(0, 1000)}"`
  }));

  const [recommendedTagsGenerate] = useMutationWithValidation<
    RecommendedTagsGenerateMutation,
    RecommendedTagsGenerateMutationVariables
  >(RecommendedTagsGenerateDocument, "generateTags");

  const aiInputsDisabled = aiInputs?.some(input => !input.value || input.value === "<p></p>");

  const handleGenerateTags = () => {
    setAiLoading(true);
    if (aiInputsFormatted && aiInputsFormatted.length > 0 && !aiInputsDisabled) {
      recommendedTagsGenerate({
        aiContentFields: aiInputsFormatted,
        tagTypes: aiTagTypes ?? Object.values(TagTypeEnum)
      }).then(res => {
        setRecommendedTags(res?.recommendedTagsGenerate?.tags ?? []);
        setAiLoading(false);
      });
    }
  };

  const arrayErrors = useMemo(() => {
    if (errors === undefined) {
      return undefined;
    }

    if (!Array.isArray(errors)) {
      return [errors];
    }
    return errors;
  }, [errors]);

  const tagsAsOptions = useCallback(
    (tags: string[] | string | null) => {
      if (single) {
        tags = tags as string | null;
        if (tags === null && allowAny) {
          return { label: intl.formatMessage({ id: "dictionary.any" }), value: tags };
        }
        return { label: tags, value: tags };
      } else {
        tags = tags as string[] | null;
        return (
          tags?.map(tag => ({
            label: tag,
            value: tag
          })) ?? []
        );
      }
    },
    [allowAny, intl, single]
  );

  const getTagsQuery = useLazyQueryWithPromise<GetTagsQuery, GetTagsQueryVariables>(GetTagsDocument);

  const loadTags = useCallback(
    (inputValue: string, callback: (options: Option[]) => void) => {
      getTagsQuery({ fetchPolicy: "network-only", variables: { searchTerm: inputValue, tagType } }).then(tagData => {
        callback(
          tagData.data.tags.map(tag => ({
            label: tag,
            value: tag
          }))
        );
      });
    },
    [getTagsQuery, tagType]
  );

  const cssColorStyles = useMemo(() => {
    if (color == "dark") {
      return {
        multiValue: {
          backgroundColor: "var(--ion-color-primary-500)",
          color: "var(--ion-color-white)"
        },
        multiValueLabel: {
          color: "var(--ion-color-white)"
        }
      };
    }
    return {
      multiValue: {
        backgroundColor: "var(--ion-color-gray-300)",
        color: "var(--ion-color-dark)"
      },
      multiValueLabel: {
        color: "var(--ion-color-dark)"
      }
    };
  }, [color]);

  const cssStyles = useMemo(
    () => ({
      multiValue: (base: any) => ({
        ...base,
        ...cssColorStyles.multiValue,
        borderRadius: "3px",
        padding: "3px"
      }),
      multiValueLabel: (base: any) => ({
        ...base,
        ...cssColorStyles.multiValueLabel
      })
    }),
    [cssColorStyles.multiValue, cssColorStyles.multiValueLabel]
  );

  const componentsSetup: Partial<SelectComponentsConfig<unknown, boolean, GroupBase<unknown>>> = {
    ...selectProps.components,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    ClearIndicator: () => null,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Input: props => <components.Input {...props} spellCheck={true} />
  };

  const defaultOptions = useMemo(() => {
    if (allowAny) {
      return [
        {
          label: intl.formatMessage({ id: "dictionary.any" }),
          value: null
        }
      ];
    }
    return undefined;
  }, [allowAny, intl]);

  return (
    <div className={styles.wrapperItem}>
      {label && (
        <div>
          <LargeLabel className={smallerLabel ? styles.label : undefined}>
            {label}
            {required && <RequiredFieldIndicator />}
          </LargeLabel>
        </div>
      )}
      {subLabel && (
        <SmallLabel>
          {subLabel}
          {required && subLabel && !label && <RequiredFieldIndicator />}
        </SmallLabel>
      )}

      <Controller
        control={control}
        name={name}
        render={({ field: { name, onChange, value } }) => (
          <div className={styles.margin}>
            <AsyncCreatableSelect
              aria-describedby={arrayErrors && arrayErrors.length > 0 ? `${name}-errors` : ""}
              aria-invalid={arrayErrors?.length ? "true" : "false"}
              aria-label={name}
              aria-required={required}
              classNamePrefix="react-select"
              closeMenuOnSelect
              defaultOptions={defaultOptions}
              isDisabled={disabled}
              isMulti={!single}
              isSearchable
              loadOptions={loadTags}
              menuPortalTarget={document.body}
              name={name}
              onChange={options => {
                if (single) {
                  const option = options as Option;
                  option.label = capitalizeFirstLetter(option.label);
                  if (option.value) {
                    option.value = capitalizeFirstLetter(option.value);
                  }
                  onChange(option.value);
                  onValueChange?.(option.value);
                } else {
                  let properlyTypedMultiOptions = options as unknown as Option[];
                  properlyTypedMultiOptions.forEach(option => {
                    option.label = capitalizeFirstLetter(option.label);
                    option.value = capitalizeFirstLetter(option.value);
                  });
                  properlyTypedMultiOptions = sortByStringField(properlyTypedMultiOptions, option => option.value);
                  onChange(properlyTypedMultiOptions.map(e => e.value));
                  onValueChange?.(properlyTypedMultiOptions.map(e => e.value));
                }
              }}
              placeholder={selectProps.placeholder ?? intl.formatMessage({ id: "dictionary.selectBestMatch" })}
              styles={{ ...selectStyles, ...cssStyles }}
              value={tagsAsOptions(value)}
              {...selectProps}
              components={componentsSetup}
            />
            {!single && aiInputsFormatted && aiInputs && aiInputsFormatted.length > 0 && (
              <div className={styles.flex}>
                <ConditionalTooltip
                  condition={!!aiInputsDisabled}
                  content={intl.formatMessage(
                    { id: "pages.admin.tags.generateDisabled" },
                    {
                      values: joinListWithCommasAndAnd(
                        aiInputs
                          .filter(input => !input.value || input.value === "<p></p>")
                          .map(input => input.description),
                        intl
                      )
                    }
                  )}
                  initialDirection="right"
                >
                  {tooltipTriggerProps => (
                    <Button
                      aria-describedby={tooltipTriggerProps?.["aria-describedby"]}
                      disabled={aiInputsDisabled || aiLoading}
                      fill="clear"
                      onClick={handleGenerateTags}
                      onIonBlur={tooltipTriggerProps?.onBlur}
                      onIonFocus={tooltipTriggerProps?.onFocus}
                      role={tooltipTriggerProps?.role}
                    >
                      <IconBase icon="microchipAi" slot="start" />
                      <FormattedMessage id="pages.admin.tags.generate" />
                    </Button>
                  )}
                </ConditionalTooltip>
                <Icon
                  hoverContent={intl.formatMessage(
                    { id: "pages.admin.tags.generateHelp" },
                    {
                      values: joinListWithCommasAndAnd(
                        aiInputsFormatted.map(input => input.description),
                        intl
                      )
                    }
                  )}
                  icon="questionCircle"
                  spacingStart
                />
                <div className={styles.flex}>
                  {recommendedTags.map(tag => (
                    <div
                      className={styles.recommendedTag}
                      key={tag}
                      onClick={() => {
                        const tags = value as string[];
                        if (!tags.some(v => v === tag)) {
                          onChange([...tags, tag]);
                        }
                      }}
                    >
                      <Tag
                        color="secondary-500"
                        icon={
                          (tagsAsOptions(value) as Option[]).some(option => option.value === tag) ? "check" : "plus"
                        }
                        text={tag}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      />
      {arrayErrors && arrayErrors.length > 0 && (
        <div id={`${name}-errors`}>{arrayErrors?.map(error => <Error error={error} key={error?.message} />)}</div>
      )}
    </div>
  );
};

export default TagSelect;
